import { ArrowRightOutlined, LoadingOutlined } from '@ant-design/icons';
import { SocialLogin } from 'components/common';
import { CustomInput } from 'components/formik';
import { SIGNIN } from 'constants/routes';
import { PLAN_OPTIONS, SOCIAL_PLAN } from 'constants/plans';
import { Field, Form, Formik } from 'formik';
import { useDocumentTitle, useScrollTop } from 'hooks';
import PropType from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signUp } from 'redux/actions/authActions';
import { setAuthenticating, setAuthStatus } from 'redux/actions/miscActions';
import * as Yup from 'yup';

const SignInSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email is not valid.')
    .required('Email is required.'),
  password: Yup.string()
    .required('Password is required.')
    .min(8, 'Password length should be at least 8 characters.')
    .matches(/[A-Z\W]/g, 'Password should contain at least 1 uppercase letter.'),
  fullname: Yup.string()
    .required('Full name is required.')
    .min(4, 'Name should be at least 4 characters.'),
  plan: Yup.string()
    .oneOf(PLAN_OPTIONS.map((plan) => plan.value))
    .required('Please choose a plan.')

});

const SignUp = ({ history }) => {
  const { isAuthenticating, authStatus } = useSelector((state) => ({
    isAuthenticating: state.app.isAuthenticating,
    authStatus: state.app.authStatus
  }));
  const dispatch = useDispatch();
  const [selectedPlan, setSelectedPlan] = useState(SOCIAL_PLAN);

  useScrollTop();
  useDocumentTitle('Sign Up | TaKKa');

  useEffect(() => () => {
    dispatch(setAuthStatus(null));
    dispatch(setAuthenticating(false));
  }, []);

  const onClickSignIn = () => history.push(SIGNIN);

  const onFormSubmit = (form) => {
    dispatch(signUp({
      fullname: form.fullname.trim(),

      email: form.email.trim().toLowerCase(),
      password: form.password.trim(),
      plan: form.plan
    }));
  };

  return (
    <div className="auth-content">
      {authStatus?.success && (
        <div className="loader">
          <h3 className="toast-success auth-success">
            {authStatus?.message}
            <LoadingOutlined />
          </h3>
        </div>
      )}
      {!authStatus?.success && (
        <>
          {authStatus?.message && (
            <h5 className="text-center toast-error">
              {authStatus?.message}
            </h5>
          )}
          <div className={`auth ${authStatus?.message && (!authStatus?.success && 'input-error')}`}>
            <div className="auth-main">
              <h3>Sign up to TaKKa</h3>
              <Formik
                initialValues={{
                  fullname: '',

                  email: '',
                  password: '',
                  plan: SOCIAL_PLAN
                }}
                validateOnChange
                validationSchema={SignInSchema}
                onSubmit={onFormSubmit}
              >
                {({ setFieldValue }) => (
                  <Form>
                    <div className="auth-field">
                      <span className="label-input">* Choose your plan</span>
                      <div className="signup-plan-options">
                        {PLAN_OPTIONS.map((plan) => (
                          <label
                            className={`signup-plan-option${selectedPlan === plan.value ? ' signup-plan-option-selected' : ''}`}
                            htmlFor={`signup-plan-${plan.value}`}
                            key={plan.value}
                          >
                            <Field
                              disabled={isAuthenticating}
                              id={`signup-plan-${plan.value}`}
                              name="plan"
                              onChange={(event) => {
                                setSelectedPlan(event.target.value);
                                setFieldValue('plan', event.target.value);
                              }}
                              type="radio"
                              value={plan.value}
                            />
                            <span>
                              <strong>{plan.label}</strong>
                              <small>{plan.description}</small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="auth-field">
                      <Field
                        disabled={isAuthenticating}
                        name="fullname"
                        type="text"
                        label="* Full Name"
                        placeholder="John Doe"
                        style={{ textTransform: 'capitalize' }}
                        component={CustomInput}
                      />
                    </div>

                    <div className="auth-field">
                      <Field
                        disabled={isAuthenticating}
                        name="email"
                        type="email"
                        label="* Email"
                        placeholder="test@example.com"
                        component={CustomInput}
                      />
                    </div>
                    <div className="auth-field">
                      <Field
                        disabled={isAuthenticating}
                        name="password"
                        type="password"
                        label="* Password"
                        placeholder="Your Password"
                        component={CustomInput}
                      />
                    </div>
                    <br />
                    <div className="auth-field auth-action auth-action-signup">
                      <button
                        className="button auth-button"
                        disabled={isAuthenticating}
                        type="submit"
                      >
                        {isAuthenticating ? 'Signing Up' : 'Sign Up'}
                        &nbsp;
                        {isAuthenticating ? <LoadingOutlined /> : <ArrowRightOutlined />}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            <div className="auth-divider">
              <h6>OR</h6>
            </div>
            <SocialLogin isLoading={isAuthenticating} registrationPlan={selectedPlan} />
          </div>
          <div className="auth-message">
            <span className="auth-info">
              <strong>Already have an account?</strong>
            </span>
            <button
              className="button button-small button-border button-border-gray"
              disabled={isAuthenticating}
              onClick={onClickSignIn}
              type="button"
            >
              Sign In
            </button>
          </div>
        </>
      )}
    </div>
  );
};

SignUp.propTypes = {
  history: PropType.shape({
    push: PropType.func
  }).isRequired
};

export default SignUp;
